import { map, pipe, prop, toPairs } from 'ramda';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAsyncFn } from 'react-use';
import { urlCopiedNotification } from '../../../notifications';
import { copyToClipboard } from '../../../../../utils/copyToClipboard';
import { useMMGRSettings } from '../../../../../utils/hoc/withMMGRSettings';
import { useTrackActions } from '../../../../../utils/hooks/bi/useTrackActions';
import { useMediaManagerSDK } from '../../../../../utils/hooks/useMediaManagerSDK';
import { useNotificatorConsumer } from '../../../../../utils/hooks/useNotificator';
import { useSources } from '../../../../../utils/hooks/useSources';
import { MediaManagerSDKFile } from '../../../../../utils/media-manager-sdk';
import { getCopyableFileUrl, hasUser } from '../../../../../utils/media-manager-sdk/items';
import { Text } from '../../../../../components/common';
import { ITEM_INFO_BOTTOM_PART_TRANSLATIONS } from './ItemInfoTable.constants';
import styles from './ItemInfoTable.scss';
import { IOuter, TItemProps } from './ItemInfoTable.types';
import { getPropsTransformers, invokeTransformerWithItem, TPropsTransformers } from './ItemInfoTable.utils';

export const ItemInfoTable: FC<IOuter> = ({ item, source }) => {
  const { formatNumber } = useIntl();
  const { mmgrSettings } = useMMGRSettings();
  const { trackManagerPanelFileInfoCopyUrlClick } = useTrackActions();

  const copyableItemUrl = useMemo(
    () => getCopyableFileUrl(item as MediaManagerSDKFile, mmgrSettings.privateMediaRestVideoFiles),
    [item, mmgrSettings.privateMediaRestVideoFiles],
  );

  const handleCopyUrlClick = useCallback(() => {
    trackManagerPanelFileInfoCopyUrlClick(item as MediaManagerSDKFile);
    return copyToClipboard(copyableItemUrl);
  }, [copyableItemUrl, trackManagerPanelFileInfoCopyUrlClick, item]);

  const handleCopyWixUrlClick = useCallback(() => {
    // trackManagerPanelFileInfoCopyWixUrlClick(item as MediaManagerSDKFile);
    return copyToClipboard(copyableItemUrl);
  }, [copyableItemUrl, trackManagerPanelFileInfoCopyUrlClick, item]);

  const { fetchUserInfoMP } = useMediaManagerSDK({ source });

  const [asyncInfo, getUserInfo] = useAsyncFn(() => fetchUserInfoMP(item).then(prop('data')), [item, fetchUserInfoMP]);

  useEffect(() => {
    if (!hasUser(item)) {
      return;
    }

    getUserInfo();
  }, [item]);

  const { notify } = useNotificatorConsumer();

  const addUrlCopiedNotification = useCallback(() => {
    notify(urlCopiedNotification());
  }, [notify]);

  const { isTrash } = useSources({ source });

  const itemProps = useMemo(() => {
    const transformers = getPropsTransformers(item);

    return pipe<TPropsTransformers, object, TItemProps>(
      map(
        invokeTransformerWithItem(item, {
          formatNumber,
          handleCopyUrlClick,
          handleCopyWixUrlClick,
          asyncInfo,
          copyableItemUrl,
          addUrlCopiedNotification,
          isTrash,
        }),
      ),
      toPairs,
    )(transformers);
  }, [
    getPropsTransformers,
    item,
    invokeTransformerWithItem,
    handleCopyUrlClick,
    asyncInfo,
    copyableItemUrl,
    addUrlCopiedNotification,
    isTrash,
  ]);

  return (
    <table className={styles.table}>
      <tbody>
        {map(
          ([key, value]) =>
            value && (
              <tr className={styles.infoRow} key={key}>
                <td data-hook={`single-item-bottom-part-option-${key}`} className={styles.optionKey}>
                  <Text weight="thin" size="tiny" secondary light={false}>
                    <FormattedMessage id={ITEM_INFO_BOTTOM_PART_TRANSLATIONS[key]} />
                  </Text>
                </td>

                <td align="right" data-hook={`single-item-bottom-part-option-${key}-value`}>
                  {key === 'createdBy' ? (
                    <Text tagName="div" size="tiny" ellipsis tooltipProps={{ appendTo: 'window' }}>
                      {value}
                    </Text>
                  ) : (
                    value
                  )}
                </td>
              </tr>
            ),
          itemProps,
        )}
      </tbody>
    </table>
  );
};
